import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@mui/material";
import { Sparkles } from "lucide-react";
import { API_BASE } from "../../../config";
import { useSessionList } from "../../toolkit/useSessionList";
import { postChairSuggest, SuggestedChair } from "../api";

const MAX_SESSIONS = 5;

/**
 * Schema chair prefill: pick up to 5 of the patient's sessions; the backend
 * (POST /experiential/chair/suggest) proposes the active modes and what each
 * says, in the client's own words. The therapist edits before generating audio.
 */
export function ChairSuggest({ patientId, language, getToken, hasContent, onSuggest }: {
  patientId: string;
  language: "en" | "hu";
  getToken: () => Promise<string | null>;
  hasContent: boolean;
  onSuggest: (chairs: SuggestedChair[]) => void;
}) {
  const { t } = useTranslation();
  const { sessions, loading, error } = useSessionList(API_BASE, getToken, patientId);
  const [picked, setPicked] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [runError, setRunError] = useState<string | null>(null);

  useEffect(() => {
    setPicked(sessions[0] ? [sessions[0].session_id] : []); // newest by default
  }, [sessions]);

  const toggle = (sid: string) => setPicked((p) => (p.includes(sid)
    ? p.filter((x) => x !== sid)
    : p.length < MAX_SESSIONS ? [...p, sid] : p));

  const run = async () => {
    if (hasContent && !window.confirm(t("experiential.suggest.replaceConfirm"))) return;
    setBusy(true);
    setRunError(null);
    try {
      onSuggest(await postChairSuggest(getToken, { patient_id: patientId, session_ids: picked, language }));
    } catch (e) {
      console.error("chair suggestion failed", e);
      const msg = e instanceof Error ? e.message : String(e);
      setRunError(msg === "consent_required" ? t("experiential.suggest.consent") : `${t("experiential.suggest.failed")}: ${msg}`);
    } finally {
      setBusy(false);
    }
  };

  if (!patientId) return null;
  return (
    <div className="exp-suggest">
      <div className="exp-suggest__title">{t("experiential.suggest.title")}</div>
      {error ? <div className="exp-suggest__error">{t("experiential.suggest.sessionsFailed")}: {error}</div>
        : !loading && sessions.length === 0 ? <div className="exp-suggest__hint">{t("experiential.suggest.noSessions")}</div>
        : (
          <>
            <div className="exp-suggest__hint">{t("experiential.suggest.hint", { n: MAX_SESSIONS })}</div>
            <div className="exp-suggest__chips">
              {sessions.map((s) => (
                <button key={s.session_id} type="button" onClick={() => toggle(s.session_id)}
                  className={`exp-suggest__chip ${picked.includes(s.session_id) ? "is-on" : ""}`}>
                  {s.session_date || s.session_id.slice(0, 6)}
                </button>
              ))}
            </div>
            <Button size="small" variant="outlined" startIcon={<Sparkles size={14} />} onClick={run}
              disabled={busy || picked.length === 0}>
              {busy ? t("experiential.suggest.running") : t("experiential.suggest.run")}
            </Button>
          </>
        )}
      {runError && <div className="exp-suggest__error">{runError}</div>}
      <style>{`
        .exp-suggest { background: var(--color-brand-light); border: 1px solid var(--color-border); border-radius: var(--radius-md);
          padding: 12px; margin-bottom: 12px; display: flex; flex-direction: column; gap: 8px; align-items: flex-start; }
        .exp-suggest__title { font-weight: 600; font-size: 14px; color: var(--color-title); }
        .exp-suggest__hint { font-size: 12px; color: var(--color-muted); }
        .exp-suggest__error { font-size: 12px; color: var(--color-danger); }
        .exp-suggest__chips { display: flex; flex-wrap: wrap; gap: 6px; }
        .exp-suggest__chip { font-size: 12px; padding: 4px 10px; border-radius: 999px; cursor: pointer;
          border: 1px solid var(--color-border); background: var(--color-surface); color: var(--color-ink); }
        .exp-suggest__chip.is-on { background: var(--color-brand); border-color: var(--color-brand); color: #fff; }
      `}</style>
    </div>
  );
}
